import { useCallback, useEffect, useRef, useState } from 'react';

export interface PaginaCursor<T> {
  items: T[];
  next_cursor: string | null;
}

export interface ListaPaginada<T> {
  itens: T[];
  /** Estado da PRIMEIRA página — é o que decide skeleton/erro/vazio da tela. */
  estado: 'carregando' | 'erro' | 'pronto';
  erro: Error | null;
  temMais: boolean;
  carregandoMais: boolean;
  /** Erro ao buscar a página seguinte: a lista já carregada continua na tela. */
  erroMais: Error | null;
  carregarMais: () => void;
  recarregar: () => void;
  /** Busca a primeira página de novo sem piscar "carregando" (algo externo mudou). */
  atualizarEmSilencio: () => void;
  definirItens: (atualizar: (atuais: T[]) => T[]) => void;
}

/**
 * Lista paginada por cursor, genérica (feed de versões, lista de incidentes).
 *
 * Trocar qualquer filtro descarta a lista e recomeça do topo; "carregar mais"
 * anexa a página seguinte sem repetir item (uma publicação entre duas páginas
 * não pode virar card duplicado). `carregar` é lido por ref: só `filtros`
 * (comparados por JSON) disparam nova consulta.
 */
export function usePaginaCursor<T, F>(
  filtros: F,
  carregar: (filtros: F, cursor: string | null, signal: AbortSignal) => Promise<PaginaCursor<T>>,
  chaveDe: (item: T) => string | number,
): ListaPaginada<T> {
  const chave = JSON.stringify(filtros);
  const [itens, setItens] = useState<T[]>([]);
  const [estado, setEstado] = useState<ListaPaginada<T>['estado']>('carregando');
  const [erro, setErro] = useState<Error | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [erroMais, setErroMais] = useState<Error | null>(null);
  const [tentativa, setTentativa] = useState(0);
  const emVoo = useRef<AbortController | null>(null);
  const carregarRef = useRef(carregar);
  carregarRef.current = carregar;
  const chaveDeRef = useRef(chaveDe);
  chaveDeRef.current = chaveDe;
  const silenciosoRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    emVoo.current?.abort();
    emVoo.current = null;
    const silencioso = silenciosoRef.current;
    silenciosoRef.current = false;
    if (!silencioso) {
      setEstado('carregando');
      setErro(null);
    }
    setErroMais(null);
    setCarregandoMais(false);
    carregarRef
      .current(JSON.parse(chave) as F, null, controller.signal)
      .then((pagina) => {
        setItens(pagina.items);
        setCursor(pagina.next_cursor);
        setEstado('pronto');
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        if (silencioso) return;
        setErro(causa instanceof Error ? causa : new Error(String(causa)));
        setEstado('erro');
      });
    return () => controller.abort();
  }, [chave, tentativa]);

  useEffect(() => () => emVoo.current?.abort(), []);

  const carregarMais = useCallback(() => {
    if (!cursor || emVoo.current || estado !== 'pronto') return;
    const controller = new AbortController();
    emVoo.current = controller;
    setCarregandoMais(true);
    setErroMais(null);
    carregarRef
      .current(JSON.parse(chave) as F, cursor, controller.signal)
      .then((pagina) => {
        setItens((atuais) => {
          const vistos = new Set(atuais.map((item) => chaveDeRef.current(item)));
          return [...atuais, ...pagina.items.filter((item) => !vistos.has(chaveDeRef.current(item)))];
        });
        setCursor(pagina.next_cursor);
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return;
        setErroMais(causa instanceof Error ? causa : new Error(String(causa)));
      })
      .finally(() => {
        if (emVoo.current === controller) emVoo.current = null;
        setCarregandoMais(false);
      });
  }, [chave, cursor, estado]);

  const recarregar = useCallback(() => setTentativa((valor) => valor + 1), []);
  const atualizarEmSilencio = useCallback(() => {
    silenciosoRef.current = true;
    setTentativa((valor) => valor + 1);
  }, []);
  const definirItens = useCallback((atualizar: (atuais: T[]) => T[]) => setItens(atualizar), []);

  return {
    itens,
    estado,
    erro,
    temMais: cursor !== null,
    carregandoMais,
    erroMais,
    carregarMais,
    recarregar,
    atualizarEmSilencio,
    definirItens,
  };
}
